import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { ArrowLeft, Upload, FlaskConical } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button, ThemedText } from '../components';
import { FormGroup, FormLabel, StyledSelect as Select } from '../components/FormStyles';
import labReportService from '../api/services/labReportService';
import labReportTemplateService from '../api/services/labReportTemplateService';
import customerService from '../api/services/customerService';
import locationService from '../api/services/locationService';
import workOrderService from '../api/services/workOrderService';
import { extractItems } from '../utils/responseUtils';

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${props => props.theme.colors.primary || props.theme.colors.tint};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: ${props => props.theme.borderRadius.md};
  opacity: 0.8;
  transition: all 0.2s;
  margin-left: -${props => props.theme.spacing.sm};
  &:hover { opacity: 1; background-color: ${props => props.theme.colors.primary}10; }
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.default};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.$active ? props.theme.colors.tint : props.theme.colors.border};
  color: ${props => props.$active ? '#fff' : props.theme.colors.muted};
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.$isDragging ? props.theme.colors.tint : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$isDragging ? `${props.theme.colors.tint}08` : 'transparent'};
  &:hover { border-color: ${props => props.theme.colors.tint}; }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
  height: 8px;
  margin-top: ${props => props.theme.spacing.sm};
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.tint};
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.theme.colors.border};
  border-top-color: ${props => props.theme.colors.tint};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  justify-content: flex-end;
`;

export const LabReportUploadPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // File upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [s3TempKey, setS3TempKey] = useState(null);

  // Config & process state
  const [templateId, setTemplateId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [workOrderId, setWorkOrderId] = useState('');
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [processing, setProcessing] = useState(false);

  // Load dropdown data on mount
  const loadDropdownData = useCallback(async () => {
    try {
      const [tRes, cRes, lRes, wRes] = await Promise.all([
        labReportTemplateService.getAll(),
        customerService.getCustomers(),
        locationService.getLocations(),
        workOrderService.getWorkOrders({ pageSize: 50 }),
      ]);
      setTemplates(extractItems(tRes) || []);
      setCustomers(extractItems(cRes) || []);
      setLocations(extractItems(lRes) || []);
      setWorkOrders(extractItems(wRes?.data || wRes) || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => { loadDropdownData(); }, [loadDropdownData]);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const allowedExtensions = ['.pdf', '.xlsx'];
    const ext = file?.name?.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (file && (allowedTypes.includes(file.type) || allowedExtensions.includes(ext))) {
      setUploadFile(file);
      setS3TempKey(null);
    } else {
      toast.error('Only PDF and Excel (.xlsx) files are supported');
    }
  };

  const handleUploadToS3 = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);

    try {
      const urlRes = await labReportService.getUploadUrl(uploadFile.name, uploadFile.size);
      const { uploadUrl, s3TempKey: key } = urlRes?.data || urlRes;
      await labReportService.uploadToS3(uploadUrl, uploadFile, setUploadProgress);
      setS3TempKey(key);
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleProcess = async () => {
    if (!s3TempKey) return;
    setProcessing(true);
    try {
      await labReportService.confirmUpload({
        s3TempKey,
        fileName: uploadFile.name,
        fileSizeBytes: uploadFile.size,
        templateId: templateId ? parseInt(templateId) : null,
        customerId: customerId ? parseInt(customerId) : null,
        locationId: locationId ? parseInt(locationId) : null,
        workOrderId: workOrderId ? parseInt(workOrderId) : null,
      });
      toast.success('Lab report uploaded and queued for processing');
      navigate('/lab-reports');
    } catch (err) {
      toast.error(err?.message || 'Failed to process lab report');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Layout>
      <PageContainer theme={theme}>
        {/* Header */}
        <HeaderSection theme={theme}>
          <BackButton theme={theme} onClick={() => navigate('/lab-reports')}>
            <ArrowLeft size={14} /> Lab Reports
          </BackButton>
          <ThemedText $size="h2" $weight="bold">Upload Lab Report</ThemedText>
        </HeaderSection>

        {/* Step 1: Select & Upload File */}
        <Card theme={theme}>
          <CardTitle theme={theme}>
            <StepNumber theme={theme} $active={!s3TempKey}>1</StepNumber>
            Select Document
          </CardTitle>

          <DropZone
            theme={theme}
            $isDragging={isDragging}
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('lab-upload-file-input').click()}
          >
            <input
              id="lab-upload-file-input"
              type="file"
              accept=".pdf,.xlsx"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) validateAndSetFile(e.target.files[0]); }}
            />
            {uploadFile ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <FlaskConical size={20} color={theme.colors.tint} />
                <ThemedText>{uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)</ThemedText>
              </div>
            ) : (
              <>
                <Upload size={32} color={theme.colors.muted} />
                <ThemedText $color="muted" style={{ marginTop: 8 }}>
                  Drop a PDF or Excel file here, or click to browse
                </ThemedText>
              </>
            )}
          </DropZone>

          {uploading && (
            <div>
              <ProgressBarContainer theme={theme}>
                <ProgressBarFill theme={theme} $progress={uploadProgress} />
              </ProgressBarContainer>
              <StatusMessage theme={theme}>
                <Spinner theme={theme} />
                Uploading to storage... {uploadProgress}%
              </StatusMessage>
            </div>
          )}

          {!s3TempKey && (
            <ButtonRow theme={theme}>
              <Button
                onClick={handleUploadToS3}
                disabled={!uploadFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </ButtonRow>
          )}
        </Card>

        {/* Step 2: Configure & Upload */}
        {s3TempKey && (
          <Card theme={theme}>
            <CardTitle theme={theme}>
              <StepNumber theme={theme} $active>2</StepNumber>
              Configure & Upload
            </CardTitle>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Template (optional)</FormLabel>
                <Select theme={theme} value={templateId} onChange={e => setTemplateId(e.target.value)}>
                  <option value="">Auto-detect</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Customer (optional)</FormLabel>
                <Select theme={theme} value={customerId} onChange={e => setCustomerId(e.target.value)}>
                  <option value="">None</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.company || `${c.firstName} ${c.lastName}`}</option>)}
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Location (optional)</FormLabel>
                <Select theme={theme} value={locationId} onChange={e => setLocationId(e.target.value)}>
                  <option value="">None</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Select>
              </FormGroup>

              <FormGroup theme={theme}>
                <FormLabel theme={theme}>Work Order (optional)</FormLabel>
                <Select theme={theme} value={workOrderId} onChange={e => setWorkOrderId(e.target.value)}>
                  <option value="">None</option>
                  {workOrders.map(w => <option key={w.id} value={w.id}>{w.workOrderNumber}: {w.title}</option>)}
                </Select>
              </FormGroup>
            </div>

            <ButtonRow theme={theme}>
              <Button $variant="secondary" onClick={() => navigate('/lab-reports')}>Cancel</Button>
              <Button onClick={handleProcess} disabled={processing}>
                {processing ? 'Processing...' : 'Upload & Process'}
              </Button>
            </ButtonRow>
          </Card>
        )}
      </PageContainer>
    </Layout>
  );
};
